import { Injectable } from '@angular/core';
import { Observable, throwError, timer, of } from 'rxjs';
import { retryWhen, mergeMap, take, switchMap, catchError } from 'rxjs/operators';

/**
 * Error types for classification
 */
export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  RATE_LIMIT = 'rate_limit',
  SERVER_ERROR = 'server_error',
  UNKNOWN = 'unknown'
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Retry configuration interface
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Base delay between retries in milliseconds */
  baseDelay: number;
  /** Maximum delay between retries in milliseconds */
  maxDelay: number;
  /** Exponential backoff multiplier */
  backoffMultiplier: number;
  /** Whether to use jitter to prevent thundering herd */
  useJitter: boolean;
  /** Error types that should trigger retries */
  retryableErrors: ErrorType[];
}

/**
 * Circuit breaker configuration
 */
export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit */
  failureThreshold: number;
  /** Time in milliseconds to wait before attempting to close circuit */
  timeout: number;
  /** Monitoring window in milliseconds */
  monitoringWindow: number;
}

/**
 * Enhanced error interface
 */
export interface EnhancedError extends Error {
  type: ErrorType;
  severity: ErrorSeverity;
  code?: string;
  details?: unknown;
  timestamp: Date;
  retryable: boolean;
  operation?: string;
  context?: unknown;
}

interface ClassifiableError {
  name?: string;
  message?: string;
  code?: string;
  status?: number;
  details?: unknown;
}

interface CircuitBreakerStats {
  state: CircuitState;
  failures: number;
  lastFailureTime: number;
  successCount: number;
}

/**
 * Circuit breaker state
 */
enum CircuitState {
  CLOSED = 'closed',
  OPEN = 'open',
  HALF_OPEN = 'half_open'
}

/**
 * Circuit breaker instance
 */
class CircuitBreaker {
  private state = CircuitState.CLOSED;
  private failures = 0;
  private lastFailureTime = 0;
  private successCount = 0;

  constructor(private config: CircuitBreakerConfig) {}

  canExecute(): boolean {
    if (this.state === CircuitState.CLOSED) {
      return true;
    }

    if (this.state === CircuitState.OPEN) {
      if (Date.now() - this.lastFailureTime >= this.config.timeout) {
        this.state = CircuitState.HALF_OPEN;
        this.successCount = 0;
        return true;
      }
      return false;
    }

    // HALF_OPEN state
    return true;
  }

  onSuccess(): void {
    this.failures = 0;
    
    if (this.state === CircuitState.HALF_OPEN) {
      this.successCount++;
      if (this.successCount >= 3) { // Require 3 successes to close
        this.state = CircuitState.CLOSED;
      }
    }
  }

  onFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.config.failureThreshold) {
      this.state = CircuitState.OPEN;
    }
  }

  getState(): CircuitState {
    return this.state;
  }

  getStats() {
    return {
      state: this.state,
      failures: this.failures,
      lastFailureTime: this.lastFailureTime,
      successCount: this.successCount
    };
  }
}

/**
 * @domain Core/Services/Shared
 * 
 * Comprehensive error handling and retry service.
 * 
 * This service provides:
 * - Intelligent error classification and handling
 * - Configurable retry mechanisms with exponential backoff
 * - Circuit breaker pattern for fault tolerance
 * - Error logging and monitoring
 * - Recovery strategies for different error types
 * - Performance impact mitigation
 * 
 * Usage:
 * ```typescript
 * // Basic retry with default configuration
 * this.errorHandling.withRetry(
 *   this.someService.riskyOperation()
 * ).subscribe(result => {
 *   console.log('Operation succeeded:', result);
 * });
 * 
 * // Custom retry configuration
 * this.errorHandling.withRetry(
 *   this.someService.riskyOperation(),
 *   {
 *     maxRetries: 5,
 *     baseDelay: 1000,
 *     retryableErrors: [ErrorType.NETWORK, ErrorType.SERVER_ERROR]
 *   }
 * ).subscribe(result => {
 *   console.log('Operation succeeded:', result);
 * });
 * 
 * // With circuit breaker
 * this.errorHandling.withCircuitBreaker(
 *   'external-api',
 *   this.externalApiService.call()
 * ).subscribe(result => {
 *   console.log('API call succeeded:', result);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  private circuitBreakers = new Map<string, CircuitBreaker>();
  private errorLog: EnhancedError[] = [];
  private readonly maxErrorLogSize = 1000;

  // Default configurations
  private readonly defaultRetryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 2,
    useJitter: true,
    retryableErrors: [
      ErrorType.NETWORK,
      ErrorType.SERVER_ERROR,
      ErrorType.RATE_LIMIT
    ]
  };

  private readonly defaultCircuitBreakerConfig: CircuitBreakerConfig = {
    failureThreshold: 5,
    timeout: 60000, // 1 minute
    monitoringWindow: 300000 // 5 minutes
  };

  // ========================================
  // ERROR CLASSIFICATION
  // ========================================

  /**
   * Classify an error based on its properties
   */
  classifyError(error: unknown, operation?: string, context?: unknown): EnhancedError {
    const err = (typeof error === 'object' && error !== null ? error : {}) as ClassifiableError;
    let type = ErrorType.UNKNOWN;
    let severity = ErrorSeverity.MEDIUM;
    let retryable = false;
    let code: string | undefined;

    // Firestore-specific errors
    if (err.code) {
      code = err.code;
      switch (err.code) {
        case 'permission-denied':
        case 'unauthenticated':
          type = ErrorType.AUTHENTICATION;
          severity = ErrorSeverity.HIGH;
          retryable = false;
          break;
        case 'not-found':
          type = ErrorType.NOT_FOUND;
          severity = ErrorSeverity.LOW;
          retryable = false;
          break;
        case 'already-exists':
          type = ErrorType.CONFLICT;
          severity = ErrorSeverity.MEDIUM;
          retryable = false;
          break;
        case 'resource-exhausted':
          type = ErrorType.RATE_LIMIT;
          severity = ErrorSeverity.HIGH;
          retryable = true;
          break;
        case 'unavailable':
        case 'deadline-exceeded':
        case 'internal':
          type = ErrorType.SERVER_ERROR;
          severity = ErrorSeverity.HIGH;
          retryable = true;
          break;
        case 'invalid-argument':
        case 'failed-precondition':
          type = ErrorType.VALIDATION;
          severity = ErrorSeverity.MEDIUM;
          retryable = false;
          break;
      }
    }

    // Network errors
    if (err.message?.includes('network') ||
        err.message?.includes('fetch') ||
        err.message?.includes('connection')) {
      type = ErrorType.NETWORK;
      severity = ErrorSeverity.HIGH;
      retryable = true;
    }

    // HTTP status codes
    if (err.status) {
      switch (Math.floor(err.status / 100)) {
        case 4:
          if (err.status === 401) {
            type = ErrorType.AUTHENTICATION;
            severity = ErrorSeverity.HIGH;
          } else if (err.status === 403) {
            type = ErrorType.AUTHORIZATION;
            severity = ErrorSeverity.HIGH;
          } else if (err.status === 404) {
            type = ErrorType.NOT_FOUND;
            severity = ErrorSeverity.LOW;
          } else if (err.status === 409) {
            type = ErrorType.CONFLICT;
            severity = ErrorSeverity.MEDIUM;
          } else if (err.status === 429) {
            type = ErrorType.RATE_LIMIT;
            severity = ErrorSeverity.HIGH;
            retryable = true;
          } else {
            type = ErrorType.VALIDATION;
            severity = ErrorSeverity.MEDIUM;
          }
          break;
        case 5:
          type = ErrorType.SERVER_ERROR;
          severity = ErrorSeverity.CRITICAL;
          retryable = true;
          break;
      }
    }

    const enhancedError: EnhancedError = {
      name: err.name || 'Error',
      message: err.message || 'Unknown error occurred',
      type,
      severity,
      code,
      details: err.details ?? error,
      timestamp: new Date(),
      retryable,
      operation,
      context
    };

    // Log the error
    this.logError(enhancedError);

    return enhancedError;
  }

  // ========================================
  // RETRY MECHANISMS
  // ========================================

  /**
   * Execute an operation with retry logic
   */
  withRetry<T>(
    operation: Observable<T>,
    config: Partial<RetryConfig> = {},
    operationName?: string,
    context?: unknown
  ): Observable<T> {
    const retryConfig = { ...this.defaultRetryConfig, ...config };

    return operation.pipe(
      retryWhen(errors => 
        errors.pipe(
          mergeMap((error, index) => {
            const enhancedError = this.classifyError(error, operationName, context);
            
            // Check if error is retryable
            if (!retryConfig.retryableErrors.includes(enhancedError.type) || 
                index >= retryConfig.maxRetries) {
              console.error(`❌ Operation failed after ${index + 1} attempts:`, enhancedError);
              return throwError(() => enhancedError);
            }

            // Calculate delay with exponential backoff
            const delay = this.calculateRetryDelay(
              index, 
              retryConfig.baseDelay, 
              retryConfig.maxDelay,
              retryConfig.backoffMultiplier,
              retryConfig.useJitter
            );

            console.warn(`⚠️ Retrying operation (attempt ${index + 1}/${retryConfig.maxRetries}) after ${delay}ms:`, enhancedError.message);
            
            return timer(delay);
          }),
          take(retryConfig.maxRetries)
        )
      )
    );
  }

  /**
   * Execute an operation with circuit breaker protection
   */
  withCircuitBreaker<T>(
    circuitName: string,
    operation: Observable<T>,
    config: Partial<CircuitBreakerConfig> = {},
    operationName?: string
  ): Observable<T> {
    const circuitConfig = { ...this.defaultCircuitBreakerConfig, ...config };
    
    if (!this.circuitBreakers.has(circuitName)) {
      this.circuitBreakers.set(circuitName, new CircuitBreaker(circuitConfig));
    }

    const circuit = this.circuitBreakers.get(circuitName)!;

    if (!circuit.canExecute()) {
      const error = this.createCircuitOpenError(circuitName, operationName);
      return throwError(() => error);
    }

    return operation.pipe(
      mergeMap(result => {
        circuit.onSuccess();
        return of(result);
      }),
      retryWhen(errors =>
        errors.pipe(
          mergeMap(error => {
            const enhancedError = this.classifyError(error, operationName, { circuitName });
            circuit.onFailure();
            
            console.error(`❌ Circuit breaker ${circuitName} recorded failure:`, enhancedError);
            return throwError(() => enhancedError);
          })
        )
      )
    );
  }

  /**
   * Combine retry and circuit breaker patterns
   */
  withRetryAndCircuitBreaker<T>(
    circuitName: string,
    operation: Observable<T>,
    retryConfig: Partial<RetryConfig> = {},
    circuitConfig: Partial<CircuitBreakerConfig> = {},
    operationName?: string,
    context?: unknown
  ): Observable<T> {
    return this.withCircuitBreaker(
      circuitName,
      this.withRetry(operation, retryConfig, operationName, context),
      circuitConfig,
      operationName
    );
  }

  // ========================================
  // ERROR RECOVERY STRATEGIES
  // ========================================

  /**
   * Execute operation with fallback
   */
  withFallback<T>(
    primaryOperation: Observable<T>,
    fallbackOperation: Observable<T>,
    operationName?: string
  ): Observable<T> {
    return primaryOperation.pipe(
      retryWhen(errors =>
        errors.pipe(
          switchMap((error, index) => {
            const enhancedError = this.classifyError(error, operationName);
            
            if (index === 0) {
              console.warn(`⚠️ Primary operation failed, trying fallback:`, enhancedError.message);
              return timer(1000).pipe(
                switchMap(() => fallbackOperation),
                catchError(() => throwError(() => enhancedError))
              );
            }
            
            return throwError(() => enhancedError);
          }),
          take(1)
        )
      )
    );
  }

  /**
   * Execute operation with timeout
   */
  withTimeout<T>(
    operation: Observable<T>,
    timeoutMs: number,
    operationName?: string
  ): Observable<T> {
    return new Observable<T>(observer => {
      const timeoutId = setTimeout(() => {
        const error = this.createTimeoutError(timeoutMs, operationName);
        observer.error(error);
      }, timeoutMs);

      const subscription = operation.subscribe({
        next: value => {
          clearTimeout(timeoutId);
          observer.next(value);
        },
        error: error => {
          clearTimeout(timeoutId);
          observer.error(this.classifyError(error, operationName));
        },
        complete: () => {
          clearTimeout(timeoutId);
          observer.complete();
        }
      });

      return () => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
      };
    });
  }

  // ========================================
  // MONITORING & ANALYTICS
  // ========================================

  /**
   * Get error statistics
   */
  getErrorStatistics(timeWindow?: number) {
    const windowStart = timeWindow ? Date.now() - timeWindow : 0;
    const relevantErrors = this.errorLog.filter(e => e.timestamp.getTime() > windowStart);

    const errorsByType = relevantErrors.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<ErrorType, number>);

    const errorsBySeverity = relevantErrors.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<ErrorSeverity, number>);

    const errorsByOperation = relevantErrors.reduce((acc, error) => {
      const op = error.operation || 'unknown';
      acc[op] = (acc[op] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: relevantErrors.length,
      timeWindow: timeWindow || 'all time',
      byType: errorsByType,
      bySeverity: errorsBySeverity,
      byOperation: errorsByOperation,
      recentErrors: relevantErrors.slice(-10) // Last 10 errors
    };
  }

  /**
   * Get circuit breaker statistics
   */
  getCircuitBreakerStatistics(): Record<string, CircuitBreakerStats> {
    const stats: Record<string, CircuitBreakerStats> = {};

    for (const [name, circuit] of this.circuitBreakers.entries()) {
      stats[name] = circuit.getStats();
    }

    return stats;
  }

  /**
   * Get all error logs
   */
  getErrorLogs(limit?: number): EnhancedError[] {
    return limit ? this.errorLog.slice(-limit) : [...this.errorLog];
  }

  /**
   * Clear error logs
   */
  clearErrorLogs(): void {
    this.errorLog = [];
    console.log('🧹 Error logs cleared');
  }

  /**
   * Reset circuit breaker
   */
  resetCircuitBreaker(circuitName: string): boolean {
    const circuit = this.circuitBreakers.get(circuitName);
    if (circuit) {
      this.circuitBreakers.set(circuitName, new CircuitBreaker(this.defaultCircuitBreakerConfig));
      console.log(`🔄 Circuit breaker ${circuitName} reset`);
      return true;
    }
    return false;
  }

  /**
   * Reset all circuit breakers
   */
  resetAllCircuitBreakers(): void {
    for (const circuitName of this.circuitBreakers.keys()) {
      this.resetCircuitBreaker(circuitName);
    }
    console.log('🔄 All circuit breakers reset');
  }

  // ========================================
  // PRIVATE HELPER METHODS
  // ========================================

  /**
   * Calculate retry delay with exponential backoff and jitter
   */
  private calculateRetryDelay(
    attempt: number,
    baseDelay: number,
    maxDelay: number,
    multiplier: number,
    useJitter: boolean
  ): number {
    let delay = baseDelay * Math.pow(multiplier, attempt);
    delay = Math.min(delay, maxDelay);

    if (useJitter) {
      // Add random jitter (±25%)
      const jitter = delay * 0.25 * (Math.random() - 0.5) * 2;
      delay += jitter;
    }

    return Math.max(delay, 0);
  }

  /**
   * Log an error
   */
  private logError(error: EnhancedError): void {
    this.errorLog.push(error);

    // Keep log size manageable
    if (this.errorLog.length > this.maxErrorLogSize) {
      this.errorLog = this.errorLog.slice(-this.maxErrorLogSize);
    }

    // Log to console based on severity
    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
        console.error('🚨 CRITICAL ERROR:', error);
        break;
      case ErrorSeverity.HIGH:
        console.error('❌ HIGH SEVERITY ERROR:', error);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('⚠️ MEDIUM SEVERITY ERROR:', error);
        break;
      case ErrorSeverity.LOW:
        break;
    }
  }

  /**
   * Create circuit open error
   */
  private createCircuitOpenError(circuitName: string, operationName?: string): EnhancedError {
    return {
      name: 'CircuitOpenError',
      message: `Circuit breaker ${circuitName} is open`,
      type: ErrorType.SERVER_ERROR,
      severity: ErrorSeverity.HIGH,
      timestamp: new Date(),
      retryable: false,
      operation: operationName,
      context: { circuitName }
    };
  }

  /**
   * Create timeout error
   */
  private createTimeoutError(timeoutMs: number, operationName?: string): EnhancedError {
    return {
      name: 'TimeoutError',
      message: `Operation timed out after ${timeoutMs}ms`,
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.HIGH,
      timestamp: new Date(),
      retryable: true,
      operation: operationName,
      context: { timeoutMs }
    };
  }
}

